import {Routes} from '@angular/router';
import {AppLayoutComponent} from '@app/layout/app-layout.component';
import {IndexComponent} from '@app/index/index.component';
import {MxRoutes} from '@aviary/store/router/router.types';
import {FavoritesComponent} from '@app/my-account/favorites/favorites.component';
import {LookupAccountComponent} from '@app/registration/lookup-account.component';
import {CreateAccountComponent} from '@app/registration/create-account.component';
import {NotLoggedInGuard} from '@app/route-guards/not-logged-in.guard';
import {CatalogComponent} from '@app/catalog/catalog.component';
import {LogoutComponent} from '@app/index/login/logout.component';
import {normalizeToAngularRoutes} from '@aviary/store/router/router.normalizer';
import {buildMxRouteConstantsFrom} from '@aviary/store/router/router.helpers';
import {ComponentLibComponent} from '@lib/component-lib/component-lib.component';
import {DashboardComponent} from '@app/my-account/dashboard/dashboard.component';
import {CheckedOutComponent} from '@app/my-account/checked-out/checked-out.component';
import {HoldsComponent} from '@app/my-account/holds/holds.component';
import {SettingsComponent} from '@app/my-account/settings/settings.component';
import {BalanceComponent} from '@app/my-account/balance/balance.component';
import {ItemRequestsComponent} from '@app/my-account/item-requests/item-requests.component';
import {MyProfileComponent} from '@app/my-account/my-profile/my-profile.component';
import {BorrowingHistoryComponent} from '@app/my-account/checked-out/borrowing-history/borrowing-history.component';
import {CatalogBibDetailsComponent} from '@app/catalog/bib-details/catalog-bib-details.component';
import {CatalogSearchComponent} from '@app/catalog/search/catalog-search.component';
import {ItemRequestDetailsComponent} from '@app/my-account/item-requests/item-request-details/item-request-details.component';
import {RouteName} from '@app/app.route-names.generated';
import {RelatedAccountsComponent} from '@app/my-account/related-accounts/related-accounts.component';
import {AuthGuard} from '@app/route-guards/auth.guard';
import {PartialAuthGuard} from '@app/route-guards/partial-auth.guard';
import {DependentDetailsComponent} from '@app/my-account/related-accounts/dependent-details/dependent-details.component';
import {DependentCheckedOutComponent} from '@app/my-account/related-accounts/dependent-details/tabs/dependent-checked-out.component';
import {DependentHoldsComponent} from '@app/my-account/related-accounts/dependent-details/tabs/dependent-holds.component';
import {DependentBalanceComponent} from '@app/my-account/related-accounts/dependent-details/tabs/dependent-balance.component';
import {NothingComponent} from '@aviary/components/nothing/nothing.component';

const mxRoutes = [ // CAUTION:  Don't give this constant an explicit type.  Its type is established at the end of this declaration.
  {
    path: '', component: AppLayoutComponent, name: 'LAYOUT_ROOT',
    children: [
      {path: '', component: IndexComponent, canActivate: [NotLoggedInGuard], name: 'INDEX'},

      {path: 'login', component: IndexComponent, canActivate: [NotLoggedInGuard], title: 'Login', name: 'LOGIN'},
      {path: 'logout', component: LogoutComponent, title: 'Logout', name: 'LOGOUT'},
      {path: 'component-lib', component: ComponentLibComponent, title: 'Component Library', name: 'COMPONENT-LIB'},
      {
        path: 'catalog', name: 'CATALOG_ROOT',
        children: [
          {path: '', component: CatalogComponent, title: 'Catalog', name: 'CATALOG'},
          {path: 'search', component: CatalogSearchComponent, title: 'Catalog Search', name: 'CATALOG_SEARCH'},
          {path: 'item/:id', component: CatalogBibDetailsComponent, title: 'Catalog', name: 'CATALOG_BIB_DETAILS'},
        ]
      },
      // registration routes
      {path: 'register', canActivate: [NotLoggedInGuard], component: CreateAccountComponent, title: 'Create Account', name: 'REGISTER'},
      {
        path: 'register-existing-card', canActivate: [PartialAuthGuard], component: LookupAccountComponent,
        title: 'Look Up Account', name: 'REGISTER_EXISTING_CARD'
      },
      {path: 'register/verify', canActivate: [PartialAuthGuard], component: NothingComponent, title: 'Verify Email', name: 'REGISTER_VERIFY_EMAIL_LANDING'},
      /*
        This 'my-account/verify-email' route is intentionally outside the following AuthGuard-protected 'my-account' hierarchy because the browser session
        that opens the verification link has no need to be authenticated.
      */
      {path: 'my-account/verify-email', component: NothingComponent, title: 'Verify Email', name: 'VERIFY_EMAIL_CHANGE_LANDING'},
      { // authenticated routes
        path: '', canActivate: [AuthGuard], name: 'HOME_ROOT',
        children: [
          {
            // Children of this route will show the "My Account" side navigation card in AppLayoutComponent.
            path: 'my-account', name: 'MY_ACCOUNT',
            children: [
              {path: 'dashboard', component: DashboardComponent, title: 'Dashboard', name: 'DASHBOARD'},
              {
                path: 'checked-out', name: 'CHECKED_OUT_ROOT',
                children: [
                  {path: '', pathMatch: 'full', component: CheckedOutComponent, title: 'Checked Out', name: 'CHECKED_OUT'},
                  {path: 'borrowing-history', component: BorrowingHistoryComponent, title: 'Borrowing History', name: 'BORROWING_HISTORY'},
                ],
              },
              {path: 'holds', component: HoldsComponent, title: 'Holds', name: 'HOLDS'},
              {path: 'balance', component: BalanceComponent, title: 'Balance', name: 'BALANCE'},
              {path: 'requests', component: ItemRequestsComponent, title: 'Requests', name: 'ITEM_REQUESTS'},
              {path: 'my-profile', component: MyProfileComponent, title: 'My Profile', name: 'MY_PROFILE'},
              {
                path: 'related-accounts', name: 'RELATED_ACCOUNTS_ROOT',
                children: [
                  {path: '', component: RelatedAccountsComponent, title: 'Related Accounts', name: 'RELATED_ACCOUNTS'},
                  {
                    path: 'dependent/:id', component: DependentDetailsComponent, title: 'Dependent Details', name: 'DEPENDENT_DETAILS',
                    tabs: [
                      {path: 'checked-out', component: DependentCheckedOutComponent, name: 'DEPENDENT_CHECKED_OUT', tabLabel: 'Checked Out'},
                      {path: 'holds', component: DependentHoldsComponent, name: 'DEPENDENT_HOLDS', tabLabel: 'Holds'},
                      {path: 'balance', component: DependentBalanceComponent, name: 'DEPENDENT_BALANCE', tabLabel: 'Balance'}
                    ]
                  },
                ]
              },
              {path: 'favorites', component: FavoritesComponent, title: 'Favorites', name: 'FAVORITES'},
              {path: 'settings', component: SettingsComponent, title: 'Settings', name: 'SETTINGS'},
            ]
          },
          // This is separate from the "requests" route, as it is an exception to the "My Account" side navigation rule.
          {path: 'my-account/requests/:id', component: ItemRequestDetailsComponent, title: 'Item Request Details', name: 'ITEM_REQUEST_DETAILS'},
        ]
      },
    ]
  },
  // fallthrough if nothing else matches redirect to '/'
  {path: '**', redirectTo: '/', pathMatch: 'full', name: 'FALL_THROUGH'}
] as const satisfies MxRoutes;

export const {PATH_FOR, CHILD_ROUTE_NAMES_FOR, ROUTER_TABS_FOR, ROUTER_TAB_INDEX_FOR} = buildMxRouteConstantsFrom(mxRoutes);

export const appRoutes: Routes = normalizeToAngularRoutes<RouteName>(mxRoutes);
